<template>
  <div ref="rootEl" class="relative flex-1 min-w-0">
    <div class="flex items-stretch rounded-lg border border-slate-500/80 bg-slate-800/55 overflow-hidden focus-within:ring-2 focus-within:ring-cyan-400/40 focus-within:border-cyan-400/70">
      <input
        v-if="!picked"
        v-model="query"
        type="search"
        autocomplete="off"
        :placeholder="placeholder"
        class="flex-1 min-w-0 px-3 py-2 bg-transparent text-slate-100 text-sm placeholder:text-slate-500 focus:outline-none"
        @focus="onFocus"
        @keydown.escape.prevent="closeDropdown"
      />
      <div
        v-else
        class="flex-1 min-w-0 px-3 py-2 text-slate-100 text-sm truncate flex items-center gap-2"
        :title="picked.label"
      >
        <span class="truncate">{{ picked.label }}</span>
        <button
          type="button"
          class="shrink-0 text-cyan-300/90 hover:text-white text-xs px-1.5 py-0.5 rounded border border-cyan-400/35"
          @click="clearPicked"
        >
          Limpar
        </button>
      </div>
    </div>
    <ul
      v-if="dropdownOpen"
      class="absolute z-40 mt-1 w-full max-h-52 overflow-y-auto rounded-lg border border-slate-600/80 bg-slate-900 shadow-xl shadow-black/50 py-1 text-sm"
      role="listbox"
    >
      <li v-if="loading" class="px-3 py-2 text-slate-400">Buscando…</li>
      <li
        v-else-if="queryTrim.length < minQueryLength"
        class="px-3 py-2 text-slate-500"
      >
        Digite pelo menos {{ minQueryLength }} caractere(s).
      </li>
      <li v-else-if="!results.length" class="px-3 py-2 text-slate-500">Nenhum resultado.</li>
      <li
        v-for="r in results"
        v-else
        :key="r.id"
        class="px-3 py-2 cursor-pointer text-slate-200 hover:bg-cyan-500/15 hover:text-white"
        role="option"
        @mousedown.prevent="choose(r)"
      >
        {{ r.label }}
      </li>
    </ul>
  </div>
</template>

<script>
import { useApi } from '../composables/useApi'

export default {
  name: 'AgentAsyncPicker',
  props: {
    kind: {
      type: String,
      required: true,
      validator: (v) => v === 'executor' || v === 'coordinator'
    },
    /** Agente em edição (ex.: coordenador): excluído da lista de coordenadores. */
    excludeId: { type: [Number, String, null], default: null },
    /** IDs já adicionados à lista permitida — não aparecem nos resultados. */
    omitIds: { type: Array, default: () => [] },
    /** Especialidades de executor a excluir (ex.: email_analyst no picker do coordenador). */
    excludeSpecialties: { type: Array, default: () => [] },
    minQueryLength: { type: Number, default: 2 },
    placeholder: { type: String, default: 'Buscar por nome…' }
  },
  emits: ['select', 'clear'],
  setup() {
    const { get } = useApi()
    return { get }
  },
  data() {
    return {
      query: '',
      results: [],
      loading: false,
      dropdownOpen: false,
      picked: null,
      debounceTimer: null,
      boundDocClick: null
    }
  },
  computed: {
    queryTrim() {
      return String(this.query || '').trim()
    }
  },
  watch: {
    query() {
      if (this.picked) return
      this.scheduleSearch()
    },
    omitIds: {
      deep: true,
      handler() {
        if (this.dropdownOpen && this.queryTrim.length >= this.minQueryLength) this.runSearch()
      }
    },
    excludeId() {
      if (this.dropdownOpen && this.queryTrim.length >= this.minQueryLength) this.runSearch()
    },
    excludeSpecialties: {
      deep: true,
      handler() {
        if (this.dropdownOpen && this.queryTrim.length >= this.minQueryLength) this.runSearch()
      }
    }
  },
  mounted() {
    this.boundDocClick = (e) => this.onDocClick(e)
    document.addEventListener('click', this.boundDocClick, true)
  },
  unmounted() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    if (this.boundDocClick) document.removeEventListener('click', this.boundDocClick, true)
  },
  methods: {
    reset() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer)
      this.query = ''
      this.results = []
      this.loading = false
      this.dropdownOpen = false
      this.picked = null
    },
    onFocus() {
      if (this.picked) return
      this.dropdownOpen = true
      if (this.queryTrim.length >= this.minQueryLength) this.runSearch()
    },
    closeDropdown() {
      this.dropdownOpen = false
    },
    onDocClick(e) {
      const el = this.$refs.rootEl
      if (!el || typeof el.contains !== 'function') return
      if (!el.contains(e.target)) this.closeDropdown()
    },
    scheduleSearch() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => this.runSearch(), 300)
    },
    async runSearch() {
      if (this.picked) return
      const q = this.queryTrim
      if (q.length < this.minQueryLength) {
        this.results = []
        this.loading = false
        return
      }
      this.loading = true
      const qs = new URLSearchParams({
        kind: this.kind,
        q
      })
      const omit = Array.isArray(this.omitIds) ? this.omitIds : []
      omit.forEach((id) => {
        const n = Number(id)
        if (Number.isFinite(n)) qs.append('omit_ids[]', String(n))
      })
      if (this.excludeId != null && this.excludeId !== '') {
        const ex = Number(this.excludeId)
        if (Number.isFinite(ex)) qs.set('exclude_id', String(ex))
      }
      const exSpecs = Array.isArray(this.excludeSpecialties) ? this.excludeSpecialties : []
      for (const s of exSpecs) {
        const t = String(s || '').trim()
        if (t) qs.append('exclude_specialties[]', t)
      }
      const { data, error } = await this.get(`/api/v1/agents/picker?${qs}`)
      this.loading = false
      if (error || !data) {
        this.results = []
        return
      }
      const rows = Array.isArray(data.data) ? data.data : []
      this.results = rows
    },
    choose(r) {
      if (!r || r.id == null) return
      this.picked = { ...r }
      this.query = ''
      this.results = []
      this.dropdownOpen = false
      this.$emit('select', this.picked)
    },
    clearPicked() {
      this.picked = null
      this.$emit('clear')
    }
  }
}
</script>
